import { StateCreator } from 'zustand';

export interface Task {
  id: number;
  title: string;
  description: string;
  patient: string;
  assignedTo: string;
  priority: 'High' | 'Medium' | 'Low';
  status: 'Pending' | 'In Progress' | 'Completed';
  dueDate: string;
  createdDate: string;
  category:
    'Assessment' | 'Medication' | 'Care Plan' | 'Scheduling' | 'Documentation';
}

export interface TasksSlice {
  tasks: Task[];
  selectedTask: Task | null;
  isTasksLoading: boolean;
  searchTerm: string;
  selectedStatus: string;
  selectedPriority: string;
  setTasks: (tasks: Task[]) => void;
  addTask: (task: Omit<Task, 'id'>) => void;
  updateTask: (id: number, updates: Partial<Task>) => void;
  deleteTask: (id: number) => void;
  setSelectedTask: (task: Task | null) => void;
  setTasksLoading: (loading: boolean) => void;
  setSearchTerm: (term: string) => void;
  setSelectedStatus: (status: string) => void;
  setSelectedPriority: (priority: string) => void;
  getFilteredTasks: () => Task[];
  getTaskStats: () => {
    total: number;
    pending: number;
    inProgress: number;
    completed: number;
  };
}

const mockTasks: Task[] = [
  {
    id: 1,
    title: 'Complete patient assessment for Sarah Johnson',
    description:
      'Perform comprehensive health assessment including vital signs, medication review, and care plan evaluation.',
    patient: 'Sarah Johnson',
    assignedTo: 'Dr. Maria Rodriguez',
    priority: 'High',
    status: 'In Progress',
    dueDate: '2024-01-20',
    createdDate: '2024-01-15',
    category: 'Assessment',
  },
  {
    id: 2,
    title: 'Review medication list for Michael Chen',
    description:
      'Review current medications, check for interactions, and update medication list.',
    patient: 'Michael Chen',
    assignedTo: 'Dr. James Wilson',
    priority: 'Medium',
    status: 'Pending',
    dueDate: '2024-01-22',
    createdDate: '2024-01-16',
    category: 'Medication',
  },
  {
    id: 3,
    title: 'Update care plan for Emily Davis',
    description:
      'Review and update the care plan based on recent health changes and treatment progress.',
    patient: 'Emily Davis',
    assignedTo: 'Dr. Maria Rodriguez',
    priority: 'Low',
    status: 'Completed',
    dueDate: '2024-01-25',
    createdDate: '2024-01-14',
    category: 'Care Plan',
  },
  {
    id: 4,
    title: 'Schedule follow-up visit for Robert Wilson',
    description:
      'Schedule next home visit and coordinate with family members for availability.',
    patient: 'Robert Wilson',
    assignedTo: 'Dr. James Wilson',
    priority: 'High',
    status: 'In Progress',
    dueDate: '2024-01-18',
    createdDate: '2024-01-17',
    category: 'Scheduling',
  },
  {
    id: 5,
    title: 'Document wound care for Lisa Thompson',
    description:
      'Document current wound status, treatment provided, and progress notes.',
    patient: 'Lisa Thompson',
    assignedTo: 'Dr. Maria Rodriguez',
    priority: 'Medium',
    status: 'Pending',
    dueDate: '2024-01-23',
    createdDate: '2024-01-16',
    category: 'Documentation',
  },
];

export const tasksSlice: StateCreator<TasksSlice> = (set, get) => ({
  tasks: mockTasks,
  selectedTask: null,
  isTasksLoading: false,
  searchTerm: '',
  selectedStatus: 'all',
  selectedPriority: 'all',

  setTasks: (tasks: Task[]) => {
    set({ tasks });
  },

  addTask: (task: Omit<Task, 'id'>) => {
    const newTask: Task = {
      ...task,
      id: Math.max(...get().tasks.map(t => t.id)) + 1,
    };
    set(state => ({ tasks: [...state.tasks, newTask] }));
  },

  updateTask: (id: number, updates: Partial<Task>) => {
    set(state => ({
      tasks: state.tasks.map(task =>
        task.id === id ? { ...task, ...updates } : task
      ),
    }));
  },

  deleteTask: (id: number) => {
    set(state => ({
      tasks: state.tasks.filter(task => task.id !== id),
    }));
  },

  setSelectedTask: (task: Task | null) => {
    set({ selectedTask: task });
  },

  setTasksLoading: (loading: boolean) => {
    set({ isTasksLoading: loading });
  },

  setSearchTerm: (term: string) => {
    set({ searchTerm: term });
  },

  setSelectedStatus: (status: string) => {
    set({ selectedStatus: status });
  },

  setSelectedPriority: (priority: string) => {
    set({ selectedPriority: priority });
  },

  getFilteredTasks: () => {
    const { tasks, searchTerm, selectedStatus, selectedPriority } = get();
    return tasks.filter(task => {
      const matchesSearch =
        task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.patient.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.assignedTo.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus =
        selectedStatus === 'all' ||
        task.status.toLowerCase().replace(' ', '-') === selectedStatus;
      const matchesPriority =
        selectedPriority === 'all' ||
        task.priority.toLowerCase() === selectedPriority;
      return matchesSearch && matchesStatus && matchesPriority;
    });
  },

  getTaskStats: () => {
    const { tasks } = get();
    return {
      total: tasks.length,
      pending: tasks.filter(t => t.status === 'Pending').length,
      inProgress: tasks.filter(t => t.status === 'In Progress').length,
      completed: tasks.filter(t => t.status === 'Completed').length,
    };
  },
});
